import { lazy, Suspense, useState, type ReactNode } from 'react'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageAssistantSurface from './OfferPageAssistantSurface'
import OfferPageAssistantExpandedContext from './OfferPageAssistantExpandedContext'

const loadOfferPageFocusEngine = () => import('./OfferPageFocusEngine')
const OfferPageFocusEngine = lazy(loadOfferPageFocusEngine)
const focusEngineFallback = null

interface Props {
  children: ReactNode
  offerPageState: OfferPageState
}

export default function OfferPageAssistantHost({ children, offerPageState }: Props) {
  // Owned here (not inside OfferPageAssistantSurface, where the equivalent `isExpanded` state
  // lives) so it can be shared with `children` -- the assistant source registrations mounted as
  // siblings of the surface -- via context, gating expensive derived data (e.g. loyalty-earn
  // totals, which trigger a real API call) the same way the legacy panel already gated it.
  const [isAssistantExpanded, setIsAssistantExpanded] = useState(false)

  return <OfferPageAssistantExpandedContext value={isAssistantExpanded}>
    {children}
    <ErrorBoundary fallback={null}>
      <OfferPageAssistantSurface offerPageState={offerPageState} onExpandedChange={setIsAssistantExpanded} />
    </ErrorBoundary>
    <ErrorBoundary fallback={null}>
      <Suspense fallback={focusEngineFallback}>
        <OfferPageFocusEngine offerPageState={offerPageState} />
      </Suspense>
    </ErrorBoundary>
  </OfferPageAssistantExpandedContext>
}
